import React from 'react';

const BlogRenderer = ({ data }) => {
    const renderBlock = (block) => {
        switch (block.type) {
            case 'header':
                const HeaderTag = `h${block.data.level}`;
                const headerStyles = {
                    h1: "text-3xl md:text-4xl font-bold my-6 text-gray-900",
                    h2: "text-2xl md:text-3xl font-bold my-5 text-gray-800",  
                    h3: "text-xl md:text-2xl font-bold my-4 text-gray-700",
                    h4: "text-lg md:text-xl font-bold my-3 text-gray-700",
                    h5: "text-base md:text-lg font-bold my-3 text-gray-600",
                    h6: "text-sm md:text-base font-bold my-2 text-gray-600"
                };
                return <HeaderTag 
                    className={headerStyles[`h${block.data.level}`] || "text-2xl font-bold my-4 text-gray-800"}
                    dangerouslySetInnerHTML={{ __html: block.data.text }}
                />;

            case 'paragraph':
                return (
                    <p 
                        className="my-4 text-gray-700 leading-relaxed text-base md:text-lg"
                        dangerouslySetInnerHTML={{ __html: block.data.text }}
                    />
                );

            case 'list':
                const ListTag = block.data.style === 'ordered' ? 'ol' : 'ul';
                const listStyle = block.data.style === 'ordered' ? 'list-decimal' : 'list-disc';
                return (
                    <ListTag className={`my-4 ml-6 ${listStyle} space-y-2`}>
                        {block.data.items.map((item, index) => (
                            <li 
                                key={index} 
                                className="text-gray-700 leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: item }}
                            />
                        ))}
                    </ListTag>
                );

            case 'image':
                // Handle different image URL formats
                const getImageUrl = (imageData) => {
                    if (imageData.file && imageData.file.url) {
                        // If it's already an absolute URL, use it
                        if (imageData.file.url.startsWith('http')) {
                            return imageData.file.url;
                        }
                        // Otherwise, prepend the base URL
                        return `https://hdmovietoday.xyz${imageData.file.url}`;
                    }
                    return null;
                };

                const imageUrl = getImageUrl(block.data);
                if (!imageUrl) return null;

                return (
                    <figure className="my-8">
                        <div className="relative overflow-hidden rounded-xl bg-gray-100 shadow-lg">
                            <img
                                src={imageUrl}
                                alt={block.data.caption || 'Blog image'}
                                className="w-full h-auto object-cover transition-transform duration-500 hover:scale-105"
                                style={{ maxHeight: '500px', minHeight: '200px' }}
                                loading="lazy"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = `
                                        <div class="flex items-center justify-center h-48 bg-gray-100">
                                            <div class="text-center">
                                                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                                <p class="mt-2 text-sm text-gray-500">Image unavailable</p>
                                            </div>
                                        </div>
                                    `;
                                }}
                            />
                        </div>
                        {block.data.caption && (
                            <figcaption 
                                className="text-center text-gray-600 mt-4 text-sm italic px-4"
                                dangerouslySetInnerHTML={{ __html: block.data.caption }}
                            />
                        )}
                    </figure>
                );

            case 'code':
                return (
                    <pre className="my-6 bg-gray-100 p-4 rounded-lg overflow-x-auto border">
                        <code className="text-sm text-gray-800">{block.data.code}</code>
                    </pre>
                );

            case 'quote':
                return (
                    <blockquote className="my-6 pl-6 border-l-4 italic bg-gray-50 py-4 pr-4 rounded-r-lg" style={{ borderLeftColor: '#e67929' }}>
                        <p 
                            className="text-gray-700 text-lg leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: block.data.text }}
                        />
                        {block.data.caption && (
                            <footer 
                                className="text-gray-600 mt-3 text-sm font-medium"
                                dangerouslySetInnerHTML={{ __html: `— ${block.data.caption}` }}
                            />
                        )}
                    </blockquote>
                );

            case 'linkTool':
                return (
                    <a
                        href={block.data.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block my-6 p-4 border rounded-lg hover:bg-gray-50 transition-colors duration-200"
                    >
                        {block.data.meta && (
                            <>
                                <h4 className="font-bold text-gray-900 mb-2">{block.data.meta.title}</h4>
                                <p className="text-gray-600 mb-2">{block.data.meta.description}</p>
                            </>
                        )}
                        <span className="text-blue-600 hover:text-blue-800 font-medium">{block.data.link}</span>
                    </a>
                );

            default:
                return null;
        }
    };

    return (
        <div className="prose prose-lg max-w-none">
            {data.blocks.map((block, index) => (
                <div key={index}>{renderBlock(block)}</div>
            ))}
        </div>
    );
};

export default BlogRenderer; 